import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="custom-fields--formula-editor"
// Manages a Trix-based formula editor where fields are inserted as ActionText attachments
export default class extends Controller {
  static targets = ["editor", "formulaInput", "fieldChip", "perTreeInnerButton", "perTreeOuterButton"];

  static values = {
    availableFields: Array, // Array of {token, id, name, sgid, level, kind}
    initialFormula: String, // The formula string like "{{123}} + {{lot_total_trees}}"
  };

  connect() {
    this.handleTrixInitBound = this.handleTrixInitBound || this.handleTrixInit.bind(this);
    this.handleTrixChangeBound = this.handleTrixChangeBound || this.handleTrixChange.bind(this);
    this.handleFileAcceptBound = this.handleFileAcceptBound || this.handleFileAccept.bind(this);
    this.handlePasteBound = this.handlePasteBound || this.handlePaste.bind(this);

    // Wait for Trix to initialize
    this.editorTarget.addEventListener("trix-initialize", this.handleTrixInitBound);
    this.editorTarget.addEventListener("trix-change", this.handleTrixChangeBound);

    // If Trix is already initialized
    if (this.editorTarget.editor) {
      this.handleTrixInit();
    }

    this.selectedPerTreeInnerByFieldId = new Map();
  }

  disconnect() {
    this.trixInitialized = false;
    this.editorTarget.removeEventListener("trix-initialize", this.handleTrixInitBound);
    this.editorTarget.removeEventListener("trix-change", this.handleTrixChangeBound);
    this.editorTarget.removeEventListener("trix-file-accept", this.handleFileAcceptBound);
    this.editorTarget.removeEventListener("trix-paste", this.handlePasteBound);
  }

  handleTrixInit() {
    if (this.trixInitialized) return;

    this.trixInitialized = true;

    // Configure Trix for formula editing
    this.configureTrixForFormulas();

    // Load initial formula by parsing it and inserting attachments
    this.loadInitialFormula();
  }

  configureTrixForFormulas() {
    // Disable file attachments
    this.editorTarget.addEventListener("trix-file-accept", this.handleFileAcceptBound);

    // Disable paste of rich content
    this.editorTarget.addEventListener("trix-paste", this.handlePasteBound);
  }

  handleFileAccept(event) {
    event.preventDefault();
  }

  handlePaste(event) {
    // Only allow plain text paste - strip any HTML
    if (event.paste && event.paste.html) {
      event.preventDefault();
      const text = event.paste.string || "";
      // Only allow valid formula characters and named context tokens.
      const sanitized = text.replace(/[^a-zA-Z0-9_+\-*\/().{}, ]/g, "");
      if (sanitized) {
        this.editorTarget.editor.insertString(sanitized);
      }
    }
  }

  loadInitialFormula() {
    const formula = this.initialFormulaValue;
    if (!formula) return;

    const editor = this.editorTarget.editor;
    if (!editor) return;

    // Clear any existing content
    editor.loadHTML("");

    // Parse the formula and insert each part
    // Formula format: "{{123}} + {{lot_total_trees}} * {{789}}"
    const parts = formula.split(/(\{\{[a-zA-Z0-9_]+\}\})/);

    parts.forEach((part) => {
      const match = part.match(/\{\{([a-zA-Z0-9_]+)\}\}/);
      if (match) {
        // This is a formula reference - insert as attachment
        const token = match[1];
        const field = this.findAvailableField(token);
        if (field) {
          this.insertFieldAttachment(field);
        } else {
          // Field not found, insert as text placeholder
          editor.insertString(part);
        }
      } else if (part) {
        // Regular text (operators, spaces, etc.)
        editor.insertString(part);
      }
    });

    // Update the hidden formula input
    setTimeout(() => {
      this.updateFormulaInput();
    }, 50);
  }

  // Insert a field attachment without adding extra space
  insertFieldAttachment(field) {
    const editor = this.editorTarget.editor;
    if (!editor) return;

    const attachmentAttributes = {
      formulaToken: String(field.token),
      content: this.createAttachmentContent(field),
      contentType: "application/vnd.actiontext.attachment",
    };

    if (field.sgid) {
      attachmentAttributes.sgid = field.sgid;
    }

    const attachment = new Trix.Attachment(attachmentAttributes);

    editor.insertAttachment(attachment);
  }

  // Insert a field as an attachment into the Trix editor (called from button click)
  insertField(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    if (button.dataset.insertingFormulaToken === "true") return;

    button.dataset.insertingFormulaToken = "true";
    window.setTimeout(() => {
      delete button.dataset.insertingFormulaToken;
    }, 250);

    const token = button.dataset.fieldToken || button.dataset.fieldId;
    const field = this.findAvailableField(token);

    if (!field) {
      console.error("Formula operand not found:", token);
      return;
    }

    this.insertFieldAttachment(field);
    this.editorTarget.editor?.insertString(" ");
    this.editorTarget.focus();
  }

  selectPerTreeInner(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const fieldId = button.dataset.perTreeFieldId;
    const innerFunction = button.dataset.perTreeInnerFunction;
    if (!fieldId || !innerFunction) return;

    this.selectedPerTreeInnerByFieldId.set(String(fieldId), innerFunction);
    this.updatePerTreePickerState(fieldId);
  }

  insertPerTreeAggregate(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const fieldId = button.dataset.perTreeFieldId;
    const outerFunction = button.dataset.perTreeOuterFunction;
    const innerFunction = this.selectedPerTreeInnerByFieldId.get(String(fieldId));
    if (!fieldId || !outerFunction || !innerFunction) return;

    const token = `per_tree_${innerFunction}_${outerFunction}_${fieldId}`;
    const field = this.findAvailableField(token);

    if (!field) {
      console.error("Formula per-tree operand not found:", token);
      return;
    }

    this.insertFieldAttachment(field);
    this.editorTarget.editor?.insertString(" ");
    this.editorTarget.focus();
  }

  clearPerTreeInner(event) {
    if (event.currentTarget.contains(event.relatedTarget)) return;

    const fieldId = event.currentTarget.dataset.perTreeFieldId;
    if (!fieldId) return;

    this.selectedPerTreeInnerByFieldId.delete(String(fieldId));
    this.updatePerTreePickerState(fieldId);
  }

  createAttachmentContent(field) {
    const levelLabel = field.level_label || field.levelLabel;
    const levelText = levelLabel ? ` (${levelLabel})` : "";
    const colorClass = this.getAttachmentColorClass(field);
    const span = document.createElement("span");
    span.className = `inline-flex items-center gap-1 px-2 py-0.5 mx-0.5 text-sm font-medium rounded-full border ${colorClass}`;
    span.setAttribute("data-formula-token", String(field.token));
    span.setAttribute("contenteditable", "false");
    span.textContent = `${field.name}${levelText}`;
    return span.outerHTML;
  }

  getAttachmentColorClass(field) {
    if (field.kind === "context") {
      return "bg-secondary/20 text-secondary border-secondary/30";
    }

    if (["aggregate", "per_tree_aggregate", "tree_presence"].includes(field.kind)) {
      return "bg-accent/20 text-accent border-accent/30";
    }

    return "bg-primary/20 text-primary border-primary/30";
  }

  updatePerTreePickerState(fieldId) {
    const selectedInner = this.selectedPerTreeInnerByFieldId.get(String(fieldId));
    const selectedInnerButton = this.perTreeInnerButtonTargets.find((button) => {
      return String(button.dataset.perTreeFieldId) === String(fieldId) && button.dataset.perTreeInnerFunction === selectedInner;
    });
    const selectedInnerLabel = selectedInnerButton?.dataset.perTreeInnerLabel;

    this.perTreeInnerButtonTargets
      .filter((button) => String(button.dataset.perTreeFieldId) === String(fieldId))
      .forEach((button) => {
        const selected = button.dataset.perTreeInnerFunction === selectedInner;
        button.classList.toggle("btn-active", selected);
        button.setAttribute("aria-pressed", selected ? "true" : "false");
      });

    this.perTreeOuterButtonTargets
      .filter((button) => String(button.dataset.perTreeFieldId) === String(fieldId))
      .forEach((button) => {
        button.disabled = !selectedInner;
        button.setAttribute("aria-disabled", selectedInner ? "false" : "true");
        button.classList.toggle("border-primary", Boolean(selectedInner));
        button.classList.toggle("bg-primary/10", Boolean(selectedInner));
        button.classList.toggle("text-primary", Boolean(selectedInner));
        button.classList.toggle("border-base-300", !selectedInner);
        button.classList.toggle("text-base-content/50", !selectedInner);
        button.textContent = selectedInnerLabel
          ? button.dataset.perTreeLabelTemplate.replace("__INNER__", selectedInnerLabel)
          : button.dataset.perTreeWaitingLabel;
      });
  }

  // Insert an operator at the cursor position
  insertOperator(event) {
    event.preventDefault();
    const operator = event.currentTarget.dataset.operator;
    const editor = this.editorTarget.editor;

    if (!editor) return;

    editor.insertString(` ${operator} `);
    this.editorTarget.focus();
  }

  insertFunction(event) {
    event.preventDefault();
    const expression = event.currentTarget.dataset.functionExpression;
    const cursorOffset = Number(event.currentTarget.dataset.cursorOffset || expression?.length || 0);
    const editor = this.editorTarget.editor;

    if (!editor || !expression) return;

    const range = editor.getSelectedRange();
    editor.insertString(` ${expression} `);
    const cursorPosition = range[0] + 1 + cursorOffset;
    editor.setSelectedRange([cursorPosition, cursorPosition]);
    this.editorTarget.focus();
  }

  // Clear the formula
  clearFormula(event) {
    event.preventDefault();
    const editor = this.editorTarget.editor;
    if (!editor) return;

    // Select all and delete
    editor.setSelectedRange([0, editor.getDocument().toString().length]);
    editor.deleteInDirection("forward");
    this.editorTarget.focus();
  }

  // Handle Trix content changes - update the hidden formula input
  handleTrixChange() {
    this.updateFormulaInput();
  }

  // Convert Trix content to formula format {{field_id}}
  updateFormulaInput() {
    const trixDocument = this.editorTarget.editor?.getDocument();
    if (!trixDocument) return;

    let formula = "";
    
    // Iterate through the document pieces
    trixDocument.getPieces().forEach((piece) => {
      if (piece.attachment) {
        // This is an attachment - extract the formula token.
        const token = this.getTokenFromAttachment(piece.attachment);
        if (token) {
          formula += `{{${token}}}`;
        }
      } else {
        // Regular text - clean up and add
        const text = piece.toString();
        // Only keep valid formula characters and named context tokens.
        formula += text.replace(/[^a-zA-Z0-9_+\-*\/().{}, ]/g, "");
      }
    });

    // Clean up extra whitespace
    formula = formula.replace(/\s+/g, " ").trim();

    // Update the hidden input
    if (this.hasFormulaInputTarget) {
      this.formulaInputTarget.value = formula;
    }
  }

  findAvailableField(token) {
    return this.availableFieldsValue.find((field) => String(field.token) === String(token) || String(field.id) === String(token));
  }

  getTokenFromAttachment(attachment) {
    const values = attachment.attributes.values;
    if (values.formulaToken) return values.formulaToken;

    return this.getTokenFromSgid(values.sgid);
  }

  // Extract formula token from the available fields using the sgid
  getTokenFromSgid(sgid) {
    const field = this.availableFieldsValue.find((f) => f.sgid === sgid);
    return field?.token || field?.id;
  }
}
